import React from "react";
import { Container, Grid, Stepper, Step, StepLabel, Box, Typography, useMediaQuery, useTheme } from "@mui/material";

import FeedIcon from "@mui/icons-material/Feed";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";

import CustomForm from "../../../helper-components/form/CustomForm.jsx";
import PaperFragment from "../../../helper-components/misc/PaperFragment.jsx";
import NavigationFooter from "../../../helper-components/misc/NavigationFooter.jsx";
import PaperText from "../../../helper-components/misc/PaperText.jsx";
import CustomButton from "../../../helper-components/input/CustomButton.jsx";

import PatientData from "./components/PatientData.jsx";
import MissingAndImplantsConfirm from "./components/MissingAndImplantsConfirm.jsx";
import GetReadyPlaque from "./components/GetReadyPlaque.jsx";
import ChartPlaque from "./components/ChartPlaque.jsx";
import GetReadyUpperJaw from "./components/GetReadyUpperJaw.jsx";
import GetReadyLowerJaw from "./components/GetReadyLowerJaw.jsx";
import PeriodontalTable from "./components/PeriodontalTable.jsx";
import ConfirmExamination from "./components/ConfirmExamination.jsx";
import BingoCelebration, { nextBingoCount, pickBingoVariant } from "./components/BingoCelebration.jsx";
import SaveData from "./components/SaveData.jsx";
import PracticeResults from "./components/PracticeResults.jsx";

import { createExamination, updateExamination, queryLatestPatientExamination } from "../../../helper-functions/api/examinationQueries.js";

import { getLanguage } from "../../../config/language/language.js";
import { getPatientDisplayName } from "../../../helper-functions/shared/patientDisplay.js";
import { useExamination } from "../../../helper-functions/context/ExaminationContext.jsx";
import { normalizeTeethData } from "../../../helper-functions/shared/teethDataNormalize.js";
import { chartHasEntries, PRACTICE_PATIENT } from "../../../helper-functions/shared/examinationDraft.js";

const HIDE_HEADER_STEP_IDS = new Set([
    "upper-right-buccal",
    "upper-left-buccal",
    "upper-right-palatal",
    "upper-left-palatal",
    "lower-right-buccal",
    "lower-left-buccal",
    "lower-right-lingual",
    "lower-left-lingual",
    "confirm",
    "save"
]);

const buildStepRegistry = ({ skipIntro = false, skipPatient = false, includeResults = false, confirmTitle = "Confirm Examination", saveTitle = "Save Data" } = {}) => {
    const steps = [];

    if (!skipIntro) {
        steps.push({ id: "intro", title: "Introduction", shortLabel: "Intro" });
    }

    if (!skipPatient) {
        steps.push({ id: "patient", title: "Select Patient", shortLabel: "Patient" });
    }

    steps.push(
        { id: "dental-chart", title: "Dental Chart - confirm missing teeth and implants", shortLabel: "Dental" },
        { id: "prep-plaque", title: "Prepare for Plaque Charting", shortLabel: "Prep Plq" },
        {
            id: "fast-plaque",
            title: "Fast Plaque Charting",
            shortLabel: "Plaque",
            skipWhen: (ctx) => !ctx.chartPlaqueSeparately
        },
        { id: "fast-calculus", title: "Fast chart - Supragingival calculus", shortLabel: "Calculus" },
        { id: "fast-erythema", title: "Fast chart - Gingival Erythema", shortLabel: "Erythema" },
        { id: "prep-upper", title: "Prepare for Upper Jaw", shortLabel: "Prep Max" },
        { id: "upper-right-buccal", title: "Upper Right Buccal", shortLabel: "18-11 B" },
        { id: "upper-left-buccal", title: "Upper Left Buccal", shortLabel: "21-28 B" },
        { id: "upper-right-palatal", title: "Upper Right Palatal", shortLabel: "18-11 P" },
        { id: "upper-left-palatal", title: "Upper Left Palatal", shortLabel: "21-28 P" },
        { id: "prep-lower", title: "Prepare for Lower Jaw", shortLabel: "Prep Mand" },
        { id: "lower-right-buccal", title: "Lower Right Buccal", shortLabel: "48-41 B" },
        { id: "lower-left-buccal", title: "Lower Left Buccal", shortLabel: "31-38 B" },
        { id: "lower-right-lingual", title: "Lower Right Lingual", shortLabel: "48-41 L" },
        { id: "lower-left-lingual", title: "Lower Left Lingual", shortLabel: "31-38 L" },
        { id: "confirm", title: confirmTitle, shortLabel: "Confirm" }
    );

    if (includeResults) {
        steps.push({ id: "results", title: "Practice charts", shortLabel: "Charts" });
    } else {
        steps.push({ id: "save", title: saveTitle, shortLabel: "Save" });
    }

    return steps.map((step) => ({
        ...step,
        hideHeader: HIDE_HEADER_STEP_IDS.has(step.id)
    }));
};

const getStepIndexById = (steps, stepId) => steps.findIndex((step) => step.id === stepId);

const formatChartingPatient = (patient) => {
    const identifier = patient?.patient_identifier?.trim();
    const name = `${patient?.first_name?.trim() || ""} ${patient?.last_name?.trim() || ""}`.trim();
    if (identifier && name) return `${identifier} - ${name}`;
    if (identifier) return `Patient ID ${identifier}`;
    if (name) return name;
    return getPatientDisplayName(patient);
};

const getNextStepIndex = (currentIndex, steps, ctx) => {
    let index = currentIndex;

    while (index < steps.length - 1) {
        index += 1;
        if (!steps[index].skipWhen?.(ctx)) {
            return index;
        }
    }

    return index;
};

const getPreviousStepIndex = (currentIndex, steps, ctx) => {
    let index = currentIndex;

    while (index > 0) {
        index -= 1;
        if (!steps[index].skipWhen?.(ctx)) {
            return index;
        }
    }

    return index;
};

export default function ExaminationCreate(props) {
    const mode = props.mode || "create";
    const examinationId = props.examinationId || null;
    const isEditMode = mode === "edit";
    const isPractice = mode === "practice";

    const steps = React.useMemo(
        () =>
            buildStepRegistry({
                skipIntro: isEditMode || isPractice,
                skipPatient: isEditMode,
                includeResults: isPractice,
                confirmTitle: isPractice ? "Finish practice" : isEditMode ? "Confirm Changes" : "Confirm Examination",
                saveTitle: isEditMode ? "Saving Changes" : "Save Data"
            }),
        [isEditMode, isPractice]
    );
    const [stepIndex, setStepIndex] = React.useState(0);
    const [syncedPatientId, setSyncedPatientId] = React.useState(null);
    const [bingo, setBingo] = React.useState(null);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

    const {
        patient,
        setPatient,
        chartPlaqueSeparately,
        setChartPlaqueSeparately,
        chartingTable,
        setChartingTable,
        summaryData,
        setSummaryData,
        applyBoundDraft,
        resetDraft,
        persistence
    } = useExamination();

    const navigationContext = React.useMemo(
        () => ({
            chartPlaqueSeparately
        }),
        [chartPlaqueSeparately]
    );

    const currentStep = steps[stepIndex];
    const visibleSteps = isSmallScreen ? 5 : 15;

    const handleStepClick = (targetIndex) => {
        if (isEditMode) {
            setStepIndex(targetIndex);
            return;
        }

        if (targetIndex < stepIndex) {
            setStepIndex(targetIndex);
        }
    };

    const handleNext = () => {
        if (bingo) return;

        if (currentStep?.id === "results") {
            props.browser.navigate("/about");
            return;
        }

        if (currentStep?.id === "confirm" && !isEditMode && !isPractice) {
            setBingo({ variant: pickBingoVariant(), count: nextBingoCount() });
            return;
        }

        setStepIndex((prev) => getNextStepIndex(prev, steps, navigationContext));
    };

    const handleBingoDone = React.useCallback(() => {
        setBingo(null);
        setStepIndex((prev) => {
            if (steps[prev]?.id !== "confirm") return prev;
            return getNextStepIndex(prev, steps, navigationContext);
        });
    }, [navigationContext, steps]);

    const handleBack = () => {
        if (bingo) return;
        setStepIndex((prev) => getPreviousStepIndex(prev, steps, navigationContext));
    };

    const resetExaminationState = () => {
        resetDraft();
        setSyncedPatientId(null);
        setStepIndex(0);
    };

    const showSnackbar = props.showSnackbar;

    const adoptPatient = React.useCallback(
        (nextPatient) => {
            if (!nextPatient || persistence === "memory") {
                return;
            }

            const hadEntries = chartHasEntries(chartingTable);
            const { cleared } = applyBoundDraft(nextPatient);
            if (cleared && hadEntries) {
                setSyncedPatientId(null);
                showSnackbar({
                    color: "info",
                    message: "Started a blank chart for this patient. The previous patient's unsaved measurements were cleared."
                });
            }
        },
        [applyBoundDraft, chartingTable, persistence, showSnackbar]
    );

    const handleSkipPlaque = () => {
        setChartPlaqueSeparately(false);
        setStepIndex((prev) => getNextStepIndex(prev, steps, { chartPlaqueSeparately: false }));
    };

    React.useEffect(() => {
        setChartingTable((prev) => normalizeTeethData(prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (currentStep?.id !== "save" || !patient?.id) {
            return;
        }

        if (isPractice || persistence === "memory" || patient.id === "practice") {
            return;
        }

        const userData = props?.user?.me || {};
        const examinationRequestDTO = {
            patient_id: patient.id,
            examiner_id: userData.id,
            teeth_data: chartingTable,
            summary_data: summaryData
        };

        const onSuccess = () => {
            resetExaminationState();
            props.browser.navigate(isEditMode ? `/database/examinations/${examinationId}/view` : `/database/examinations`);
        };

        if (isEditMode && examinationId) {
            updateExamination(props, { id: examinationId, body: examinationRequestDTO }, onSuccess);
        } else {
            createExamination(props, { body: examinationRequestDTO }, onSuccess);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep?.id, patient?.id]);

    React.useEffect(() => {
        if (isEditMode || isPractice || currentStep?.id !== "dental-chart" || !patient?.id || syncedPatientId === patient.id) {
            return;
        }

        queryLatestPatientExamination(props, patient.id, (latestExamination) => {
            if (latestExamination?.teeth_data) {
                props.showSnackbar({ message: "Synced missing teeth and implants from previous examination.", color: "info" });

                setChartingTable((prev) => {
                    const updated = normalizeTeethData(prev);
                    const previousTeethData = latestExamination.teeth_data;

                    Object.keys(previousTeethData).forEach((toothNum) => {
                        if (updated[toothNum] && previousTeethData[toothNum]) {
                            const prevTooth = previousTeethData[toothNum];

                            if (prevTooth.missing !== undefined) {
                                updated[toothNum] = {
                                    ...updated[toothNum],
                                    missing: prevTooth.missing
                                };
                            }

                            if (prevTooth.implant !== undefined) {
                                updated[toothNum] = {
                                    ...updated[toothNum],
                                    implant: prevTooth.implant
                                };
                            }
                        }
                    });

                    return updated;
                });
            }

            setSyncedPatientId(patient.id);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep?.id, patient?.id, syncedPatientId, isEditMode]);

    const markFieldRecorded = React.useCallback(
        (fieldName) => {
            setSummaryData((prev) => {
                if (prev.recording?.[fieldName] === "recorded") return prev;
                return {
                    ...prev,
                    recording: {
                        ...prev.recording,
                        [fieldName]: "recorded"
                    }
                };
            });
        },
        [setSummaryData]
    );
    const markCalculusRecorded = React.useCallback(() => markFieldRecorded("calculus"), [markFieldRecorded]);
    const markErythemaRecorded = React.useCallback(() => markFieldRecorded("erythema"), [markFieldRecorded]);

    const decideBackDisabled = () => stepIndex <= 0 || Boolean(bingo);

    const decideNextDisabled = () => {
        if (currentStep?.id === "intro" && patient !== null) {
            return true;
        }

        if (currentStep?.id === "patient") {
            return patient === undefined || patient === null;
        }

        if (currentStep?.id === "prep-plaque") {
            return !chartPlaqueSeparately;
        }

        if (currentStep?.id === "save" || bingo) {
            return true;
        }

        return false;
    };

    const renderStepContent = () => {
        switch (currentStep?.id) {
            case "intro":
                return patient ? (
                    <PaperFragment
                        p={2}
                        value={
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <PaperText
                                        whiteSpace="pre-wrap"
                                        value={`You have an examination in progress.\n\nPatient: ${getPatientDisplayName(patient)}\n\nWould you like to continue or start a new examination?`}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CustomButton
                                        {...props}
                                        title="Start New Examination"
                                        onClick={resetExaminationState}
                                        variant="outlined"
                                        fullWidth
                                        className="button-large-v2"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <CustomButton
                                        {...props}
                                        title="Continue Examination"
                                        onClick={() => setStepIndex(getStepIndexById(steps, "dental-chart"))}
                                        variant="contained"
                                        fullWidth
                                        className="button-large-v2"
                                    />
                                </Grid>
                            </Grid>
                        }
                    />
                ) : (
                    <PaperText p={2} whiteSpace="pre-wrap" value={getLanguage().EXAMINATION_CREATE_ABOUT} />
                );

            case "patient":
                return (
                    <PaperFragment
                        p={2}
                        value={
                            <PatientData
                                {...props}
                                state={patient}
                                setState={isPractice ? setPatient : adoptPatient}
                                handleNext={handleNext}
                                practicePatient={isPractice ? PRACTICE_PATIENT : null}
                            />
                        }
                    />
                );

            case "dental-chart":
                return <PaperFragment value={<MissingAndImplantsConfirm state={chartingTable} setState={setChartingTable} />} />;

            case "prep-plaque":
                return (
                    <PaperFragment
                        value={
                            <GetReadyPlaque
                                handleNext={handleNext}
                                handleSkipPlaque={handleSkipPlaque}
                                chartSeparately={chartPlaqueSeparately}
                                setChartSeparately={setChartPlaqueSeparately}
                            />
                        }
                    />
                );

            case "fast-plaque":
                return (
                    <PaperFragment
                        value={
                            <ChartPlaque
                                state={chartingTable}
                                setState={setChartingTable}
                                field="plaque"
                                title="Fast Plaque Charting"
                            />
                        }
                    />
                );

            case "fast-calculus":
                return (
                    <PaperFragment
                        value={
                            <ChartPlaque
                                state={chartingTable}
                                setState={setChartingTable}
                                field="calculus"
                                title="Fast chart - Supragingival calculus"
                                activeColor="#6d4c41"
                                onInteracted={markCalculusRecorded}
                            />
                        }
                    />
                );

            case "fast-erythema":
                return (
                    <PaperFragment
                        value={
                            <ChartPlaque
                                state={chartingTable}
                                setState={setChartingTable}
                                field="erythema"
                                title="Fast chart - Gingival Erythema"
                                activeColor="#c62828"
                                onInteracted={markErythemaRecorded}
                            />
                        }
                    />
                );

            case "prep-upper":
                return <PaperFragment value={<GetReadyUpperJaw />} />;

            case "upper-right-buccal":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="upper-right-buccal" />}
                    />
                );

            case "upper-left-buccal":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="upper-left-buccal" />}
                    />
                );

            case "upper-right-palatal":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="upper-right-palatal" />}
                    />
                );

            case "upper-left-palatal":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="upper-left-palatal" />}
                    />
                );

            case "prep-lower":
                return <PaperFragment value={<GetReadyLowerJaw />} />;

            case "lower-right-buccal":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="lower-right-buccal" />}
                    />
                );

            case "lower-left-buccal":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="lower-left-buccal" />}
                    />
                );

            case "lower-right-lingual":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="lower-right-lingual" />}
                    />
                );

            case "lower-left-lingual":
                return (
                    <PaperFragment
                        value={<PeriodontalTable state={chartingTable} setState={setChartingTable} chartSeparately={chartPlaqueSeparately} variant="lower-left-lingual" />}
                    />
                );

            case "confirm":
                return <PaperFragment value={<ConfirmExamination title={isPractice ? "Finish the practice chart? Nothing will be saved." : "Save data and continue?"} />} />;

            case "results":
                return (
                    <PracticeResults
                        teethData={chartingTable}
                        summaryData={summaryData}
                        patient={patient}
                        openAppDrawer={props.sidebar.openAppDrawer}
                    />
                );

            case "save":
                return (
                    <PaperFragment
                        value={
                            <SaveData
                                title={isPractice ? "Practice complete. Nothing was saved." : "Saving, you will be shortly redirected..."}
                            />
                        }
                    />
                );

            default:
                return null;
        }
    };

    const visibleStepIndices =
        visibleSteps < steps.length
            ? Array.from({ length: visibleSteps }, (_, i) => Math.max(0, Math.min(stepIndex - Math.floor(visibleSteps / 2) + i, steps.length - 1))).filter(
                  (value, index, array) => array.indexOf(value) === index
              )
            : Array.from({ length: steps.length }, (_, i) => i);

    return (
        <Container maxWidth="xl" sx={{ mt: 2, mb: 2 }}>
            <Grid container spacing={0.5}>
                {!currentStep?.hideHeader && (
                    <Grid item xs={12}>
                        <CustomForm
                            title={isPractice ? "Practice patient" : isEditMode ? getLanguage().EXAMINATION_EDIT_TITLE : getLanguage().EXAMINATION_CREATE_TITLE}
                            subtitle={
                                isPractice
                                    ? "Choose the practice patient, then chart a full examination. The charts at the end are not stored, and the chart starts blank each time you open it."
                                    : isEditMode
                                      ? getLanguage().EXAMINATION_EDIT_SUBTITLE
                                      : getLanguage().EXAMINATION_CREATE_SUBTITLE
                            }
                            icon={<FeedIcon />}
                        />
                    </Grid>
                )}

                <Grid item xs={12}>{renderStepContent()}</Grid>

                <Grid item xs={12}>
                    <PaperFragment
                        p={1}
                        value={
                            <Box>
                                <Typography variant="h6" gutterBottom align="center" style={{ fontSize: "1.2rem" }}>
                                    {`Step ${stepIndex + 1} of ${steps.length}`}: {currentStep?.title}
                                </Typography>
                                {patient && (
                                    <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 0.5 }}>
                                        {formatChartingPatient(patient)}
                                    </Typography>
                                )}

                                <Stepper
                                    activeStep={visibleSteps < steps.length ? Math.min(stepIndex, Math.floor(visibleSteps / 2)) : stepIndex}
                                    alternativeLabel
                                    nonLinear
                                    sx={{ mt: 2 }}>
                                    {visibleStepIndices.map((targetIndex) => (
                                        <Step
                                            key={steps[targetIndex].id}
                                            completed={targetIndex < stepIndex}
                                            sx={{ cursor: isEditMode ? "pointer" : targetIndex < stepIndex ? "pointer" : "default" }}
                                            onClick={() => handleStepClick(targetIndex)}>
                                            <StepLabel
                                                StepIconProps={visibleSteps < steps.length ? { icon: targetIndex + 1 } : undefined}
                                                optional={
                                                    <Typography variant="caption" sx={{ fontSize: "0.6rem", lineHeight: 1, mt: 0.25 }}>
                                                        {steps[targetIndex].shortLabel}
                                                    </Typography>
                                                }
                                            />
                                        </Step>
                                    ))}
                                </Stepper>
                            </Box>
                        }
                    />
                </Grid>

                <NavigationFooter
                    rows={2}
                    options={[
                        {
                            onClick: handleBack,
                            title: "Back",
                            variant: "outlined",
                            icon: <NavigateBeforeIcon />,
                            disabled: decideBackDisabled()
                        },
                        {
                            onClick: handleNext,
                            title: currentStep?.id === "results" ? "Done" : "Next",
                            variant: "contained",
                            icon: <NavigateNextIcon />,
                            disabled: decideNextDisabled()
                        },
                        isEditMode
                            ? {
                                  path: `/database/examinations/${examinationId}/view`,
                                  title: "Cancel Edit",
                                  variant: "outlined",
                                  icon: <FeedIcon />
                              }
                            : {
                                  path: `/database/examinations`,
                                  title: "Examinations",
                                  variant: "outlined",
                                  icon: <FeedIcon />
                              },
                        {
                            path: `/database/patients`,
                            title: "Patients",
                            variant: "outlined",
                            icon: <PermContactCalendarIcon />
                        }
                    ]}
                />
            </Grid>
            {bingo && <BingoCelebration variantIndex={bingo.variant} count={bingo.count} onDone={handleBingoDone} />}
        </Container>
    );
}
